import { z } from 'zod';

export const authTypes = [
  'api_key',
  'bearer_token',
  'basic_auth',
  'key_secret_pair',
  'oauth2',
  'service_account_json',
  'connection_string',
  'google_auth_initiation',
  'openai',
  'custom',
] as const;

export const secretAuthTypeSchema = z.enum(authTypes);
export type SecretAuthType = (typeof authTypes)[number];

export type FieldDescriptor = {
  name: string;
  label: string;
  description: string;
  sensitive: boolean;
  example?: string;
};

export type ShapeSchema = {
  fields: FieldDescriptor[];
  usage_notes?: string;
};

export const BUILT_IN_SHAPES: Record<SecretAuthType, ShapeSchema> = {
  api_key: {
    fields: [
      {
        name: 'value',
        label: 'API Key',
        sensitive: true,
        description: 'The API key value used in request headers or query parameters.',
        example: 'sk-...',
      },
    ],
  },
  bearer_token: {
    fields: [
      {
        name: 'token',
        label: 'Bearer Token',
        sensitive: true,
        description:
          'Token sent in the Authorization header as "Bearer <token>".',
      },
    ],
  },
  basic_auth: {
    fields: [
      {
        name: 'username',
        label: 'Username',
        sensitive: false,
        description: 'The username for HTTP Basic authentication.',
      },
      {
        name: 'password',
        label: 'Password',
        sensitive: true,
        description: 'The password for HTTP Basic authentication.',
      },
    ],
  },
  key_secret_pair: {
    fields: [
      {
        name: 'key',
        label: 'Key / Access Key ID',
        sensitive: false,
        description: 'The public key or access key identifier.',
        example: 'AKIAIOSFODNN7EXAMPLE',
      },
      {
        name: 'secret',
        label: 'Secret / Access Secret',
        sensitive: true,
        description: 'The private secret that authenticates the key.',
      },
    ],
  },
  oauth2: {
    fields: [
      {
        name: 'client_id',
        label: 'Client ID',
        sensitive: false,
        description: 'The OAuth 2.0 client identifier registered with the provider.',
      },
      {
        name: 'client_secret',
        label: 'Client Secret',
        sensitive: true,
        description: 'The OAuth 2.0 client secret used to obtain tokens.',
      },
      {
        name: 'token_url',
        label: 'Token URL',
        sensitive: false,
        description: "The provider's token endpoint URL.",
        example: 'https://oauth2.provider.com/token',
      },
      {
        name: 'refresh_token',
        label: 'Refresh Token',
        sensitive: true,
        description:
          'Long-lived refresh token for obtaining new access tokens without user interaction.',
      },
      {
        name: 'scopes',
        label: 'Scopes',
        sensitive: false,
        description: 'Comma-separated list of OAuth permission scopes.',
        example: 'read:data,write:data',
      },
    ],
    usage_notes:
      'Exchange client_id + client_secret + refresh_token at token_url to obtain a short-lived access token. Include scopes in the request body.',
  },
  service_account_json: {
    fields: [
      {
        name: 'type',
        label: 'Type',
        sensitive: false,
        description: 'Credential type, typically "service_account".',
        example: 'service_account',
      },
      {
        name: 'project_id',
        label: 'Project ID',
        sensitive: false,
        description: 'The GCP project this service account belongs to.',
      },
      {
        name: 'private_key',
        label: 'Private Key',
        sensitive: true,
        description: 'RSA private key in PEM format for signing JWTs.',
      },
      {
        name: 'client_email',
        label: 'Client Email',
        sensitive: false,
        description: 'Service account email address used as the token subject.',
      },
    ],
    usage_notes:
      'Use the Google Auth library to create a JWT signed with private_key, then exchange it for an access token at the Google token endpoint. client_email is the "sub" claim.',
  },
  connection_string: {
    fields: [
      {
        name: 'url',
        label: 'Connection URL',
        sensitive: true,
        description:
          'Full connection string including protocol, credentials, host, port, and database name.',
        example: 'postgresql://user:pass@host:5432/dbname',
      },
    ],
  },
  google_auth_initiation: {
    fields: [
      {
        name: 'client_id',
        label: 'Client ID',
        sensitive: false,
        description: 'The Google OAuth 2.0 client identifier.',
      },
      {
        name: 'client_secret',
        label: 'Client Secret',
        sensitive: true,
        description: 'The Google OAuth 2.0 client secret.',
      },
      {
        name: 'refresh_token',
        label: 'Refresh Token',
        sensitive: true,
        description: 'Google OAuth 2.0 refresh token to obtain access tokens.',
      },
      {
        name: 'developer_token',
        label: 'Developer Token',
        sensitive: true,
        description: 'Developer token for accessing the Google Ads API (optional).',
      },
      {
        name: 'customer_id',
        label: 'Customer ID',
        sensitive: false,
        description: 'Google Ads Customer ID (optional).',
        example: '123-456-7890',
      },
    ],
    usage_notes:
      'Google-specific authentication shape. Used to initiate and refresh Google OAuth 2.0 sessions, including Google Ads API access.',
  },
  openai: {
    fields: [
      {
        name: 'api_key',
        label: 'API Key',
        sensitive: true,
        description: 'OpenAI API key.',
        example: 'sk-proj-...',
      },
      {
        name: 'organization_id',
        label: 'Organization ID',
        sensitive: false,
        description: 'Optional OpenAI organization identifier.',
        example: 'org-...',
      },
      {
        name: 'project_id',
        label: 'Project ID',
        sensitive: false,
        description: 'Optional OpenAI project identifier.',
        example: 'proj-...',
      },
    ],
    usage_notes:
      'Include api_key in the Authorization header as "Bearer <api_key>". Optionally pass organization_id as "OpenAI-Organization" and project_id as "OpenAI-Project" headers.',
  },
  custom: {
    fields: [],
    usage_notes:
      'Custom credential shape. Field definitions and usage are documented per-secret.',
  },
};

export const fieldDescriptorSchema = z.object({
  name: z.string().min(1, 'Field name is required'),
  label: z.string().min(1, 'Label is required'),
  description: z.string(),
  sensitive: z.boolean(),
  example: z.string().optional(),
});

export const shapeSchema = z.object({
  fields: z.array(fieldDescriptorSchema),
  usage_notes: z.string().optional(),
});

export const secretSchema = z.object({
  id: z.number().int().positive().optional(),
  key_name: z
    .string()
    .min(1, 'Key name is required')
    .max(255)
    .regex(/^[A-Z0-9_]+$/, 'Key name must be uppercase letters, numbers, and underscores only'),
  display_name: z.string().max(255).optional(),
  auth_type: secretAuthTypeSchema,
  shape_schema: shapeSchema,
  payload: z.record(z.string(), z.unknown()).refine(
    (val) => Object.keys(val).length > 0,
    'Payload must have at least one field'
  ),
  description: z.string().max(500).optional(),
});

export type SecretInput = z.infer<typeof secretSchema>;
export const secretCreateSchema = secretSchema;
export type SecretCreateInput = z.infer<typeof secretCreateSchema>;
